import React from "react";
import { Box, Typography, Card, Chip, Avatar, LinearProgress, Stack, IconButton, Divider } from "@mui/joy";
import { MessageCircle, ChevronRight, MapPin, User } from "lucide-react";
import { motion } from "framer-motion";
import { IProjectList } from "../../../../interfaces";

interface ProjectMobileCardProps {
    project: IProjectList;
    onClick: (id: string | number) => void;
}

const getStatusColor = (status: string) => {
    switch (status) {
        case "COMPLETED": return "success";
        case "IN-PROGRESS": return "primary";
        case "CANCELLED": return "danger";
        case "NOT-STARTED": return "warning";
        default: return "neutral";
    }
};

export const ProjectMobileCard = ({ project, onClick }: ProjectMobileCardProps) => {
    const statusColor = getStatusColor(project.Status);
    
    const completedTasks = project.TaskSummary?.COMPLETED || 0;
    const totalTasks = project.TaskSummary?.TotalTasks || 0;
    const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    return (
        <motion.div
            whileTap={{ scale: 0.98 }}
            onClick={() => onClick(project.Id)}
            style={{ width: "100%", cursor: "pointer" }}
        >
            <Card
                variant="outlined"
                sx={{
                    p: 0,
                    overflow: "hidden",
                    "--Card-radius": "12px",
                    bgcolor: "background.surface",
                    transition: "all 0.2s",
                    border: "1px solid",
                    borderColor: "divider",
                    "&:active": {
                        bgcolor: "background.level1",
                    }
                }}
            >
                <Box sx={{ display: "flex", width: "100%" }}>
                    {/* Status Indicator Bar */}
                    <Box 
                        sx={{ 
                            width: 6, 
                            bgcolor: `${statusColor}.softBg`, 
                            borderRight: "1px solid", 
                            borderColor: `${statusColor}.outlinedBorder` 
                        }} 
                    />
                    
                    <Box sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {/* Header: Title & Status */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography level="title-md" sx={{ fontWeight: 600, color: "text.primary" }}>
                                    {project.Name}
                                </Typography>
                                <Typography level="body-xs" sx={{ mt: 0.5, color: "text.tertiary" }}>
                                    ID: #{project.Id}
                                </Typography>
                            </Box>
                            <Chip
                                size="sm"
                                variant="soft"
                                color={statusColor as any}
                                sx={{ fontWeight: "bold", borderRadius: "6px" }}
                            >
                                {project.Status.replace("-", " ")}
                            </Chip>
                        </Box>

                        {/* Customer & Salesman */}
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <MapPin size={14} color="var(--joy-palette-text-tertiary)" />
                                <Typography level="body-xs" noWrap sx={{ maxWidth: "160px" }}>
                                    {project.CustomerStreet}, {project.CustomerCity}
                                </Typography>
                            </Box>
                            
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography level="body-xs" sx={{ color: "text.secondary" }}>
                                    {project.SalesmanName || "Unassigned"}
                                </Typography>
                                <Avatar 
                                    size="sm" 
                                    variant="soft" 
                                    sx={{ "--Avatar-size": "24px", fontSize: "10px" }}
                                >
                                    {project.SalesmanName?.charAt(0) || <User size={12} />}
                                </Avatar>
                            </Stack>
                        </Box>

                        <Divider />

                        {/* Footer: Progress & Actions */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                    <Typography level="body-xs" sx={{ fontWeight: 500 }}>
                                        {progress}% Progress
                                    </Typography>
                                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                                        {completedTasks}/{totalTasks} Tasks
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    determinate
                                    value={progress}
                                    color={statusColor as any}
                                    thickness={6}
                                    sx={{ borderRadius: "4px" }}
                                />
                            </Box>
                            
                            <Stack direction="row" spacing={0.5}>
                                <IconButton size="sm" variant="plain" color="neutral">
                                    <MessageCircle size={18} />
                                    {(project as any).NoteCount > 0 && (
                                        <Typography level="body-xs" sx={{ ml: 0.5 }}>{(project as any).NoteCount}</Typography>
                                    )}
                                </IconButton>
                                <IconButton size="sm" variant="plain" color="neutral">
                                    <ChevronRight size={18} />
                                </IconButton>
                            </Stack>
                        </Box>
                    </Box>
                </Box>
            </Card>
        </motion.div>
    );
};

export const MobileProjectList = ({ 
    projects, 
    onProjectClick 
}: { 
    projects: IProjectList[], 
    onProjectClick: (id: string | number) => void 
}) => {
    return (
        <Stack spacing={2} sx={{ p: 2, pb: 10 }}>
            {projects.map((project) => (
                <ProjectMobileCard 
                    key={project.Id} 
                    project={project} 
                    onClick={onProjectClick} 
                />
            ))}
        </Stack>
    );
};
